import React, { useState, useEffect } from "react";
import api from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { ShieldOff, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable dialog for adding a block/watch entry. Used from:
 *   - AdminSecurityEvents recent-row action buttons (prefills actor + optionally ip)
 *   - ActorListsPanel "Add manually" button (blank prefill)
 *
 * The confirm button is intentionally destructive-styled (rose) for kind='block'
 * and info-styled (sky) for kind='watch' so users can't mistake one for the
 * other at a glance.
 */
export default function BlockActorDialog({
  open, onClose, onSaved,
  prefillEmail = "", prefillIp = "", defaultKind = "block",
}) {
  const [kind, setKind] = useState(defaultKind);
  const [actorEmail, setActorEmail] = useState("");
  const [ip, setIp] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [expiry, setExpiry] = useState("24h");
  const [saving, setSaving] = useState(false);

  // Reset form fields whenever the dialog is re-opened.
  useEffect(() => {
    if (open) {
      setKind(defaultKind);
      setActorEmail(prefillEmail || "");
      setIp(prefillIp || "");
      setReason("");
      setNote("");
      setExpiry(defaultKind === "block" ? "24h" : "7d");
    }
  }, [open, defaultKind, prefillEmail, prefillIp]);

  const submit = async () => {
    if (!actorEmail && !ip) {
      toast.error("Provide at least an actor email or IP address.");
      return;
    }
    // The "anonymous" pseudo-actor comes from unauthenticated 401s — blocking
    // it wholesale would lock out legitimate first-time visitors. Guard against
    // this footgun explicitly.
    if (actorEmail === "anonymous") {
      toast.error("'anonymous' isn't a real actor — block by IP instead.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/actor-lists", {
        actor_email: actorEmail || null,
        ip: ip || null,
        kind,
        reason,
        note,
        expiry_preset: expiry,
      });
      toast.success(kind === "block"
        ? `Blocked ${actorEmail || ip}`
        : `Watching ${actorEmail || ip}`);
      onSaved?.(data.entry);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add entry");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="block-actor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {kind === "block" ? (
              <><ShieldOff className="text-rose-600" size={18}/>Block actor</>
            ) : (
              <><Eye className="text-sky-600" size={18}/>Watch actor</>
            )}
          </DialogTitle>
          <DialogDescription>
            {kind === "block"
              ? "Blocked emails and IPs are rejected with 403 on every /api/* request."
              : "Watched actors are log-only — useful for shadow-monitoring before promoting to a block."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Kind toggle */}
          <div>
            <Label className="text-xs text-slate-600 mb-1.5 block">Action</Label>
            <RadioGroup value={kind} onValueChange={setKind} className="flex gap-4" data-testid="block-kind-radio">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <RadioGroupItem value="block" data-testid="kind-block-radio"/>
                <span className="text-rose-700 font-medium">Block</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <RadioGroupItem value="watch" data-testid="kind-watch-radio"/>
                <span className="text-sky-700 font-medium">Watch</span>
              </label>
            </RadioGroup>
          </div>

          <div>
            <Label htmlFor="al-email" className="text-xs text-slate-600">Actor email <span className="text-slate-400 font-normal">(optional if IP set)</span></Label>
            <Input id="al-email" value={actorEmail} onChange={(e) => setActorEmail(e.target.value)}
                   placeholder="attacker@example.com" data-testid="block-email-input" className="mt-1"/>
          </div>

          <div>
            <Label htmlFor="al-ip" className="text-xs text-slate-600">IP address <span className="text-slate-400 font-normal">(optional if email set)</span></Label>
            <Input id="al-ip" value={ip} onChange={(e) => setIp(e.target.value)}
                   placeholder="203.0.113.42" data-testid="block-ip-input" className="mt-1"/>
          </div>

          <div>
            <Label htmlFor="al-reason" className="text-xs text-slate-600">Reason</Label>
            <Input id="al-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                   placeholder="e.g. repeated rate-limit hits" maxLength={200}
                   data-testid="block-reason-input" className="mt-1"/>
          </div>

          <div>
            <Label htmlFor="al-note" className="text-xs text-slate-600">Note <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea id="al-note" value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="Any additional context" maxLength={500} rows={2}
                      data-testid="block-note-input" className="mt-1"/>
          </div>

          <div>
            <Label className="text-xs text-slate-600 mb-1.5 block">Expiry</Label>
            <Select value={expiry} onValueChange={setExpiry}>
              <SelectTrigger data-testid="block-expiry-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">1 hour</SelectItem>
                <SelectItem value="24h">24 hours</SelectItem>
                <SelectItem value="7d">7 days</SelectItem>
                <SelectItem value="never">Never (permanent)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="block-cancel-btn">Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || (!actorEmail && !ip)}
            className={kind === "block"
              ? "bg-rose-600 hover:bg-rose-700 text-white"
              : "bg-sky-600 hover:bg-sky-700 text-white"}
            data-testid="block-submit-btn"
          >
            {saving && <Loader2 className="animate-spin mr-2" size={14}/>}
            {kind === "block" ? "Block actor" : "Add to watchlist"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
